import React, { useCallback, useMemo } from 'react';
import date from 'date-and-time';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { useGetFansReleasesQuery } from '../../../redux/api/fymAPI';
import { FansReleaseRow } from './FansReleaseRow';
import { fansMessages as messages } from './fansMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Fans.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FansReleasesPageProps {
  accountId: number;
  cursor: string | null;
  /** Set on the last loaded page: "Load more" asks for the page after it. */
  onLoadMore?: (cursor: string) => void;
}

/** One page of the releases list. The last page shows "Load more" while `nextCursor` is set. */
const FansReleasesPageComponent: React.FC<FansReleasesPageProps> = ({ accountId, cursor, onLoadMore }) => {
  const intl = useIntl();
  const { data, isLoading, error, refetch } = useGetFansReleasesQuery({ accountId, cursor });
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);
  const today = useMemo(() => date.format(new Date(), 'YYYY-MM-DD', true), []);

  const rows = useMemo(
    () =>
      (data?.releases ?? []).map((release) => <FansReleaseRow key={release.uuid} release={release} today={today} />),
    [data, today]
  );

  if (isLoading) {
    return <LoadingView small />;
  }
  if (error !== undefined) {
    return <ErrorView error={error} retry={retry} />;
  }
  if (cursor === null && rows.length === 0) {
    return <p className={styles.releasesNone}>{intl.formatMessage(messages.releasesNone)}</p>;
  }

  const nextCursor = data?.nextCursor ?? null;
  return (
    <>
      {rows}
      {onLoadMore !== undefined && nextCursor !== null ? (
        <div className={styles.loadMore}>
          <SMLButton size="sm" outline omitCap onClick={() => onLoadMore(nextCursor)}>
            {intl.formatMessage(messages.loadMore)}
          </SMLButton>
        </div>
      ) : null}
    </>
  );
};

export const FansReleasesPage = TypedMemo(FansReleasesPageComponent);
